/* eslint-disable @typescript-eslint/no-explicit-any */
import { memo, useState, useEffect } from 'react';
import useMenu from 'hooks/useMenu';
import { ListItemButton, ListItemIcon, ListItemText, Typography, Collapse } from '@mui/material';
import { ItemChildrenProps } from 'interface/menu/menuInfo';
import { useLocation } from 'react-router-dom';
import MenuItem from './MenuItem';

interface IMenuCollapseProps {
  item: ItemChildrenProps;
  level: number;
}

function MenuCollapse({ item, level }: IMenuCollapseProps): JSX.Element {
  const { open } = useMenu();
  const [collapse, setCollapse] = useState(true);
  const handleCollapse = () => {
    setCollapse(!collapse);
  };

  const Icon = item.icon;
  const itemIcon = item.icon ? <Icon style={{ fontSize: '1.25rem' }} /> : false;
  const [isSelected, setSelected] = useState(false);
  const location = useLocation();
  useEffect(() => {
    setSelected(location.pathname.includes(item.url));
  }, [item.url, location.pathname]);

  const textColor = 'text.primary';
  const iconSelectedColor = 'mind.blue.azul';
  const iconColor = 'mind.menu.icon';

  const childrenList = item.children?.map(menuItem => {
    switch (menuItem.type) {
      case 'collapse':
        return <MenuCollapse key={menuItem.id} item={menuItem} level={level + 1} />;
      case 'item':
        return <MenuItem key={menuItem.id} item={menuItem} level={level + 1} />;
      default:
        return null;
    }
  });
  return (
    <>
      <ListItemButton
        disabled={item?.disabled || false}
        onClick={handleCollapse}
        selected={isSelected}
        sx={theme => ({
          zIndex: 1201,
          margin: '0rem 1rem',
          padding: '0.625rem 1rem',
          height: 40,
          borderRadius: '0.375rem',
          '&::before': {
            content: '""',
            position: 'absolute',
            left: '-1rem',
            width: '0.25rem',
            height: '2.5rem',
            borderRadius: 0,
            background: isSelected ? `${theme.palette.mind.blue.azul}` : 'transparent',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: '50%',
            display: 'block',
            width: '0.42em',
            height: '0.42em',
            border: '1px solid',
            borderBottom: 0,
            borderLeft: 0,
            transform: collapse ? 'translateY(-50%) rotate(135deg)' : 'translateY(-50%) rotate(45deg)',
            right: '1rem',
          },
          ...(open && {
            '&:hover': {
              bgcolor: 'primary.lighter',
            },
            '&.Mui-selected': {
              bgcolor: 'primary.lighter',
              color: iconSelectedColor,
              '&:hover': {
                color: iconSelectedColor,
                bgcolor: 'primary.lighter',
              },
            },
          }),
          ...(!open && {
            '&:hover': {
              bgcolor: 'transparent',
            },
            '&.Mui-selected': {
              '&:hover': {
                bgcolor: 'transparent',
              },
              bgcolor: 'transparent',
            },
          }),
        })}
      >
        {itemIcon && (
          <ListItemIcon
            sx={{
              minWidth: 28,
              color: iconColor,
              fontSize: '20px',
              ...(!open && {
                borderRadius: 1.5,
                width: 36,
                height: 36,
                alignItems: 'center',
                justifyContent: 'center',
                '&:hover': {
                  bgcolor: 'secondary.lighter',
                },
              }),
              ...(!open &&
                isSelected && {
                  bgcolor: 'primary.lighter',
                  '&:hover': {
                    bgcolor: 'primary.lighter',
                  },
                }),
            }}
          >
            {itemIcon}
          </ListItemIcon>
        )}
        {(open || (!open && level !== 1)) && (
          <ListItemText
            primary={
              <Typography
                sx={{
                  color: isSelected ? iconSelectedColor : textColor,
                  fontSize: 14,
                  fontWeight: isSelected ? 700 : 400,
                }}
              >
                {item.title}
              </Typography>
            }
          />
        )}
      </ListItemButton>
      <Collapse in={collapse} timeout="auto" unmountOnExit>
        {childrenList}
      </Collapse>
    </>
  );
}

export default memo(MenuCollapse);
